import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  GridItem,
  Heading,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { css } from '@emotion/react'

import 'swiper/swiper-bundle.css'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { makeServerProps } from '@/server/query'

import ReferralSourceQ from '@/queries/ReferralSourceQ'
import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import makePage from '@/client/services/pages/makePage'

import WavyHeading from '@/components/About/WavyHeading'
import KhanAcademyLogo from '@/components/Icons/KhanAcademyLogo'
import Layout from '@/components/Layout/Layout'
import ShwGrid from '@/components/Layout/ShwGrid'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'

const ReferralPartner: React.FC = () => {
  const query = useQuery()

  // TODO: This query is pretty bad, can we optimize it?
  const californiaLeaderboard = query.many`
      FROM ${ReferralSourceQ} LEFT JOIN
      (SELECT ${UserQ.referral_source_id}, COUNT(*) as statistic
          FROM ${SessionRegistrationQ}
              JOIN ${UserQ} ON ${SessionRegistrationQ.user_id} = ${UserQ.id}
              JOIN ${SessionAttendeeQ} ON ${SessionRegistrationQ.session_id} = ${SessionAttendeeQ.id}
          AND EXISTS(SELECT 1 FROM (SELECT ${SessionAttendeeQ.user_id} AS registrant) AS reg_info WHERE reg_info.registrant = ${SessionRegistrationQ.user_id})
          WHERE ${UserQ.referral_source_id} IS NOT NULL
          GROUP BY ${UserQ.referral_source_id}) as refs
      ON refs.referral_source_id = ${ReferralSourceQ.id}
      WHERE ${ReferralSourceQ.partner_slug} = 'california'
      ORDER BY statistic DESC, ${ReferralSourceQ.id} ASC
      LIMIT 10
  `

  return (
    <Layout title="Free Tutoring for California Students">
      <Flex alignItems="center">
        <Container>
          <ShwGrid alignItems="center" justifyItems="center">
            <GridItem
              colSpan={[12, 12, 12, 5]}
              marginTop={[0, 0, 0, '62px']}
              marginBottom={[0, 0, 0, '62px']}
            >
              <Center>
                <Box>
                  <Stack
                    direction="row"
                    align="center"
                    css={css`
                      @media only screen and (max-width: 350px) {
                        display: none !important;
                      }
                    `}
                  >
                    <Text as="span" variant="eyebrow">
                      From the Founder Of
                    </Text>
                    <ShwLink variant="unstyled" href="https://khanacademy.org" paddingTop="5px">
                      <KhanAcademyLogo />
                    </ShwLink>
                  </Stack>
                  <Heading size="3xl" as="h1" my="16px">
                    Sharing Free Tutoring Across California
                  </Heading>
                  <Text mt="24px" mb="8px" fontSize="16px">
                    Schoolhouse.world is a non-profit that provides free, small-group tutoring to
                    students the world in math and SAT prep. Students must be at least 13 years old
                    to participate.
                  </Text>
                  <Text mb="24px" fontSize="16px">
                    Please join our efforts in sharing this resource across the Golden State!
                  </Text>
                  <Button
                    target="_blank"
                    rel="noreferrer"
                    href="https://form.typeform.com/to/iyszyWku"
                    variant="solid"
                    as="a"
                    width={['100%', 'auto']}
                  >
                    Sign up your region
                  </Button>
                </Box>
              </Center>
            </GridItem>
            <GridItem colSpan={[12, 12, 12, 7]}>
              <Center>
                <PublicImage
                  src="/CaliforniaHero-v2.png"
                  alt="Join Schoolhouse for virtual, small-group tutoring in SAT® prep, math, and more!"
                  maxWidth="100%"
                />
              </Center>
            </GridItem>
          </ShwGrid>
        </Container>
      </Flex>
      <Box background="bg.muted" py="32px">
        <Container>
          <Box h="18px">
            <WavyHeading text="How it works" width={150} color="primary.fg" />
          </Box>
          <SimpleGrid columns={[1, 2, 2, 4]} spacing="32px" mt="64px">
            <Center>
              <Stack direction="column" align="center" textAlign="center" spacing="8px">
                <Box minH="170px">
                  <PublicImage src="/about/Learn1.svg" />
                </Box>
                <Center color="gray.fg" fontWeight={600} maxW="300px" minH="60px">
                  Discover–or request–sessions in topics you need help in
                </Center>
              </Stack>
            </Center>
            <Center>
              <Stack direction="column" align="center" textAlign="center" spacing="8px">
                <Box minH="170px">
                  <PublicImage src="/about/Learn2.svg" />
                </Box>
                <Center color="gray.fg" fontWeight={600} maxW="300px" minH="60px">
                  Get help in high school math and SAT Prep–completely for free
                </Center>
              </Stack>
            </Center>
            <Center>
              <Stack direction="column" align="center" textAlign="center" spacing="8px">
                <Box minH="170px">
                  <PublicImage src="/about/Learn3.svg" />
                </Box>
                <Center color="gray.fg" fontWeight={600} maxW="300px" minH="60px">
                  Join learners from around the world
                </Center>
              </Stack>
            </Center>
            <Center>
              <Stack direction="column" align="center" textAlign="center" spacing="8px">
                <Box minH="170px">
                  <PublicImage src="/about/Learn4.svg" />
                </Box>
                <Center color="gray.fg" fontWeight={600} maxW="300px" minH="60px">
                  Regular practice will help keep you on top of your game
                </Center>
              </Stack>
            </Center>
          </SimpleGrid>
        </Container>
      </Box>
      <Container>
        <Box h="18px">
          <WavyHeading text="Spread the word" width={180} color="yellow.solid" />
        </Box>
        <Text mt="48px" mb="16px">
          Let&apos;s work together to get free tutoring in the hands of the students who need it
          most! To get started, we’ll help you create a custom welcome page for students in your
          region. All students who join Schoolhouse through your page will be added to the
          leaderboard below.
        </Text>
        <Text mb="16px">
          Once you have that, you can get creative! We recommend sharing with teachers, parents,
          students, organizations and after school programs using whichever communication outlets
          work best for you—whether that&apos;s emails, social media posts, or pizza parties! Feel
          free to reach out to{' '}
          <ShwLink href="mailto:partner@schoolhouse.world">partner@schoolhouse.world</ShwLink> for
          support and materials.
        </Text>
        <Flex justify="center">
          <Button
            target="_blank"
            rel="noreferrer"
            href="https://form.typeform.com/to/iyszyWku"
            variant="solid"
            as="a"
            width={['100%', 'auto']}
          >
            Create your custom page
          </Button>
        </Flex>
        <Box mt="64px" mb="32px">
          <Box
            borderRadius="8px 8px 0 0"
            bgColor="primary.solid"
            padding={['16px', '24px']}
            textAlign="center"
          >
            <Heading size="xl" as="h3" color="fg.contrast" fontWeight="normal">
              Engagement by Region
            </Heading>
          </Box>
          <Box
            border={`1px solid ${chakraTokenToVar('border')}`}
            padding={['10px', '30px']}
            borderRadius="0px 0px 8px 8px"
          >
            <Table>
              <Thead>
                <Tr>
                  <Th p={3}>Region</Th>
                  <Th overflowWrap="anywhere" p={3}>
                    # Sessions Attended
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {californiaLeaderboard.map((item, index) => (
                  <Tr key={item(ReferralSourceQ.description) ?? ''}>
                    <Td p={3} minW="100px" w="50%">
                      <ShwLink href={`/join/${item(ReferralSourceQ.slug)}`}>
                        {item(ReferralSourceQ.description)}
                      </ShwLink>
                      {index === 0 ? ' 🥇' : index === 1 ? ' 🥈' : index === 2 ? ' 🥉' : null}
                    </Td>
                    <Td p={3}>{item`statistic` ?? 0}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </Container>
    </Layout>
  )
}

const ReferralPartnerPage = makePage(ReferralPartner, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(ReferralPartnerPage)
export default ReferralPartnerPage
